import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Avatar, Box, Stack, Typography, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { getBrandDetails } from '../../services/brand-service';
import { changePassword } from '../../services/user-service';
import { persistor, RootState, store } from '../../store';
import { revertAll } from '../../store/actions';
import { hideLoader } from '../../store/reducers/appSlice';
import { setBrandDetails } from '../../store/reducers/brandSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { updateLoginStatus } from '../../store/reducers/userSlice';
import { trackLogout, trackPasswordChange, trackPasswordChangeFailed } from '../../utils/google-analytics';
import { clearStoragePreservingMigrationCookies } from '../../utils/migration-cookies';
import { NavigationConfig } from '../../utils/navigation-constants';
import {
    computePasswordStrength,
    getPasswordStrengthChecks,
    getPasswordStrengthMessage,
    mapYupValidationErrors,
    PASSWORD_SPECIAL_CHARACTER_REGEX,
    PASSWORD_SPECIAL_CHARACTERS,
} from '../../utils/passwordFormHelpers';
import ButtonComponent from '../button/ButtonComponent';
import { PassWordGuidelinesWrapper } from '../global-styles/GlobalStyles';
import { TextFieldComponent } from '../text-field';
import { colors } from '../theme/theme';
import CommonHeader from '../ui-components/CommonHeader';
import { PASSWORD_MAX_LENGTH, getPasswordMaxLengthMessage } from '../validations/validations';

// Password validation schema - uses shared constants (no hardcoded credential values)
const passwordValidationSchema = Yup.object().shape({
    oldPassword: Yup.string().required('Current password is required'),
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .max(PASSWORD_MAX_LENGTH, getPasswordMaxLengthMessage())
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[0-9]/, 'Password must contain at least one number')
        .matches(PASSWORD_SPECIAL_CHARACTER_REGEX, 'Password must contain at least one special character')
        .required('New password is required'),
    confirmPassword: Yup.string()
        .required('This field is empty')
        .oneOf([Yup.ref('password')], "Passwords don't match."),
});

interface ChangePasswordProps {
    onPasswordChangeComplete?: (oldPassword: string, newPassword: string) => void;
    onBack?: () => void;
}

interface PasswordInputFieldProps {
    label: string;
    value: string;
    error?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}

// Shared markup for the "New Password" / "Confirm New Password" fields to avoid
// duplicating the max-length aware TextFieldComponent wiring for each field.
const PasswordInputField: React.FC<PasswordInputFieldProps> = ({ label, value, error, onChange, onPaste }) => (
    <Box sx={{ mb: 3, textAlign: 'left' }}>
        <TextFieldComponent
            label={label}
            type="password"
            value={value}
            onChange={onChange}
            onPaste={onPaste}
            variant="outlined"
            required
            maxLength={PASSWORD_MAX_LENGTH}
            error={!!error}
            errorValue={error}
        />
    </Box>
);

export const ChangePassword: React.FC<ChangePasswordProps> = ({ onPasswordChangeComplete }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [oldPassword, setOldPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
    const requirementChecks = useMemo(() => getPasswordStrengthChecks(password), [password]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validatePasswords = async () => {
        try {
            await passwordValidationSchema.validate({ oldPassword, password, confirmPassword }, { abortEarly: false });
            setPasswordErrors({});
            return true;
        } catch (error) {
            setPasswordErrors(mapYupValidationErrors(error));
            return false;
        }
    };

    const handleLogout = async () => {
        // Track before the store is purged below - userDetails won't exist afterwards
        const currentUserId = (store.getState() as RootState).user.userDetails?.Id;
        trackLogout('password_changed', currentUserId ? String(currentUserId) : undefined);

        // Clear Redux store
        dispatch(revertAll());
        dispatch(updateLoginStatus(false));

        // Clear persisted state
        persistor.purge();

        // Clear localStorage (preserve migration cookies so user lands on login page)
        clearStoragePreservingMigrationCookies();

        // Navigate to login
        navigate(NavigationConfig.Login);

        // Fetch brand details after logout
        try {
            const response = await getBrandDetails(false);
            if (response.statusCode === 200 && response.data) {
                dispatch(setBrandDetails(response.data));
            }
        } catch (error) {
            console.error('Error fetching brand details after logout:', error);
        }
    };

    const isFormValid = () => {
        // Form is valid when:
        // 1. All three password fields have values
        // 2. New password meets all requirements
        // 3. Confirm password matches new password
        // 4. No errors exist
        // 5. Not currently submitting
        if (!oldPassword || !password || !confirmPassword || isSubmitting) {
            return false;
        }
        if (Object.keys(passwordErrors).length > 0) {
            return false;
        }
        const strength = computePasswordStrength(password);
        return strength === 5 && password === confirmPassword;
    };

    const handlePasswordSubmit = async () => {
        // Prevent multiple submissions
        if (isSubmitting) {
            return;
        }

        const isValid = await validatePasswords();
        if (!isValid) {
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await changePassword(
                {
                    oldPassword,
                    newPassword: password,
                },
                true,
            );

            if (response.statusCode === 200 || response.statusCode === 201) {
                // Track successful password change
                trackPasswordChange();

                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || 'Password Changed Successfully.',
                        severity: 'success',
                    }),
                );

                // Handle password change completion callback if provided
                if (onPasswordChangeComplete) {
                    onPasswordChangeComplete(oldPassword, password);
                }

                // Logout the application after successful password change
                // Wait a moment for the toast to be displayed before logging out
                setTimeout(() => {
                    handleLogout();
                }, 500);
            } else {
                const errorMessage = response.message || 'Failed to Change Password';
                trackPasswordChangeFailed(errorMessage);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: errorMessage,
                        severity: 'error',
                    }),
                );
                setIsSubmitting(false);
            }
        } catch (error: unknown) {
            console.error('Error changing password:', error);
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            const errorMessage =
                err?.response?.data?.message || err?.message || 'Error Changing Password. Please Try Again.';
            trackPasswordChangeFailed(errorMessage);
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: errorMessage,
                    severity: 'error',
                }),
            );
            setIsSubmitting(false);
        } finally {
            dispatch(hideLoader());
        }
    };

    // Shared onPaste guard for the password/confirmPassword fields: both fields
    // reject pasted text longer than the allowed max length in the same way.
    const createMaxLengthPasteHandler =
        (field: 'password' | 'confirmPassword') => (e: React.ClipboardEvent<HTMLInputElement>) => {
            const pastedText = e.clipboardData.getData('text');
            if (pastedText.length > PASSWORD_MAX_LENGTH) {
                e.preventDefault();
                setPasswordErrors((prev: Record<string, string>) => ({
                    ...prev,
                    [field]: getPasswordMaxLengthMessage(),
                }));
            }
        };

    const getPasswordStrengthColor = () => {
        if (!password || password.length === 0) {
            return colors.greyPreset[20];
        }
        const strength = computePasswordStrength(password);
        if (strength < 2) return colors.errorPreset[10];
        if (strength < 5) return colors.warningPreset[10];
        return colors.successPreset[10];
    };

    return (
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            sx={{
                width: '100%',
                gap: 3,
            }}
        >
            {/* Left Side - Form */}
            <Box sx={{ width: '100%' }} maxWidth={{ xs: '100%', lg: '300px' }}>
                <CommonHeader mainTitle="Change Password" mainTitleFS={20} />
                {/* Subtitle */}
                <Typography variant="body2" mb={3} color="textSecondary" lineHeight={'21px'}>
                    {
                        "Enter your current password and a new password below. Make sure it's strong and something you haven't used before."
                    }
                </Typography>

                {/* Current Password Field */}
                <Box sx={{ mb: 3, textAlign: 'left' }}>
                    <TextFieldComponent
                        label="Current Password"
                        type="password"
                        value={oldPassword}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setOldPassword(e.target.value);
                            // Clear errors when user starts typing
                            if (passwordErrors.oldPassword) {
                                setPasswordErrors((prev: Record<string, string>) => ({ ...prev, oldPassword: '' }));
                            }
                        }}
                        variant="outlined"
                        required
                        error={!!passwordErrors.oldPassword}
                        errorValue={passwordErrors.oldPassword}
                    />
                </Box>

                {/* New Password Field */}
                <PasswordInputField
                    label="New Password"
                    value={password}
                    error={passwordErrors.password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;
                        if (value.length <= PASSWORD_MAX_LENGTH) {
                            setPassword(value);
                            if (passwordErrors.password) {
                                setPasswordErrors((prev: Record<string, string>) => ({ ...prev, password: '' }));
                            }
                            // Clear confirm password error if new password is being corrected
                            if (confirmPassword && value !== confirmPassword) {
                                setPasswordErrors((prev: Record<string, string>) => ({
                                    ...prev,
                                    confirmPassword: "Passwords don't match.",
                                }));
                            }
                        } else {
                            setPasswordErrors((prev: Record<string, string>) => ({
                                ...prev,
                                password: getPasswordMaxLengthMessage(),
                            }));
                        }
                    }}
                    onPaste={createMaxLengthPasteHandler('password')}
                />

                {/* Confirm Password Field */}
                <PasswordInputField
                    label="Confirm New Password"
                    value={confirmPassword}
                    error={passwordErrors.confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;
                        if (value.length <= PASSWORD_MAX_LENGTH) {
                            setConfirmPassword(value);
                            // Display real-time error if passwords don't match
                            if (password && value !== password) {
                                setPasswordErrors((prev: Record<string, string>) => ({
                                    ...prev,
                                    confirmPassword: "Passwords don't match.",
                                }));
                            } else if (value && password && value === password) {
                                // Clear error when passwords match
                                setPasswordErrors((prev: Record<string, string>) => {
                                    const updated = { ...prev };
                                    delete updated.confirmPassword;
                                    return updated;
                                });
                            }
                        } else {
                            setPasswordErrors((prev: Record<string, string>) => ({
                                ...prev,
                                confirmPassword: getPasswordMaxLengthMessage(),
                            }));
                        }
                    }}
                    onPaste={createMaxLengthPasteHandler('confirmPassword')}
                />

                {/* Change Password Button */}
                <ButtonComponent variant="contained" onClick={handlePasswordSubmit} disabled={!isFormValid()}>
                    Change Password
                </ButtonComponent>
            </Box>

            {/* Right Side - Password Guidelines */}
            <PassWordGuidelinesWrapper
                tabIndex={0}
                sx={{
                    p: { xs: 2.5, sm: 3 },
                    border: `1px solid ${colors.bluePreset[60]}`,
                    borderRadius: 3,
                    background: `linear-gradient(160deg, ${colors.bluePreset[50]} 0%, ${colors.fullWhite} 55%)`,
                    boxShadow: '0 8px 24px rgba(0, 30, 96, 0.08)',
                }}
            >
                <Stack width={'100%'}>
                    <Stack direction={'row'} alignItems={'center'} gap={1.5} mb={1.5}>
                        <Avatar
                            sx={{
                                width: 44,
                                height: 44,
                                bgcolor: colors.fullWhite,
                                boxShadow: '0 2px 8px rgba(31, 108, 201, 0.25)',
                            }}
                        >
                            <span className="icon-tip" style={{ color: colors.bluePreset[30], fontSize: 24 }} />
                        </Avatar>
                        <Typography variant="h5" sx={{ textAlign: 'start', color: theme.palette.text.primary }}>
                            Password Guidelines
                        </Typography>
                    </Stack>

                    {/* Guidelines List */}
                    <Typography variant="caption" mb={2} color="textSecondary">
                        A password must be between 8 and {PASSWORD_MAX_LENGTH} characters long, include at least one
                        lowercase letter, at least one uppercase letter, at least one number, and at least one special
                        character
                    </Typography>
                    <Box>
                        <Typography
                            variant="subtitle1"
                            mb={1.5}
                            sx={{ textAlign: 'start', color: theme.palette.text.primary }}
                        >
                            How to meet the requirements
                        </Typography>
                        <Stack sx={{ textAlign: 'start' }} gap={1}>
                            {[
                                {
                                    label: 'Length',
                                    checkIndex: 0,
                                    description: `Your password must be between 8 and ${PASSWORD_MAX_LENGTH} characters.`,
                                },
                                {
                                    label: 'Uppercase letter',
                                    checkIndex: 2,
                                    description: 'Must include at least one uppercase letter (e.g., A, B, C).',
                                },
                                {
                                    label: 'Lowercase letter',
                                    checkIndex: 1,
                                    description: 'Must include at least one lowercase letter (e.g., a, b, c).',
                                },
                                {
                                    label: 'Number',
                                    checkIndex: 3,
                                    description: 'Must include at least one numerical digit (e.g., 1, 2, 3).',
                                },
                                {
                                    label: 'Special character',
                                    checkIndex: 4,
                                    description: (
                                        <>
                                            Must include at least one special character. Allowed characters:
                                            <Box
                                                component="span"
                                                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}
                                            >
                                                {PASSWORD_SPECIAL_CHARACTERS.map(character => (
                                                    <Box
                                                        key={character}
                                                        component="span"
                                                        sx={{
                                                            minWidth: 22,
                                                            px: 0.5,
                                                            textAlign: 'center',
                                                            fontFamily: 'monospace',
                                                            fontSize: 13,
                                                            fontWeight: 600,
                                                            lineHeight: '22px',
                                                            color: colors.bluePreset[10],
                                                            backgroundColor: colors.bluePreset[50],
                                                            border: `1px solid ${colors.bluePreset[60]}`,
                                                            borderRadius: '6px',
                                                        }}
                                                    >
                                                        {character}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </>
                                    ),
                                },
                            ].map(requirement => {
                                const met = requirementChecks[requirement.checkIndex];
                                return (
                                    <Box
                                        key={requirement.label}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1.25,
                                            p: 1.25,
                                            borderRadius: 2,
                                            backgroundColor: met ? colors.successPreset[40] : colors.fullWhite,
                                            border: `1px solid ${met ? colors.successPreset[20] : colors.greyPreset[20]}`,
                                            transition: 'background-color 0.2s ease, border-color 0.2s ease',
                                        }}
                                    >
                                        {met ? (
                                            <CheckCircleIcon
                                                sx={{ fontSize: 20, mt: '1px', color: colors.successPreset[10] }}
                                            />
                                        ) : (
                                            <RadioButtonUncheckedIcon
                                                sx={{ fontSize: 20, mt: '1px', color: colors.greyPreset[40] }}
                                            />
                                        )}
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                component="span"
                                                sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                                            >
                                                {requirement.label}:{' '}
                                            </Typography>
                                            <Typography variant="caption" component="span" color="textSecondary">
                                                {requirement.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                    <Box sx={{ mt: 3 }}>
                        <Typography
                            variant="subtitle1"
                            mb={1}
                            sx={{ textAlign: 'start', color: theme.palette.text.primary }}
                        >
                            Password Strength
                        </Typography>
                        <Box
                            sx={{
                                width: '100%',
                                height: '6px !important',
                                backgroundColor: colors.greyPreset[20],
                                borderRadius: 1,
                                overflow: 'hidden',
                                mb: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${(computePasswordStrength(password) / 5) * 100}%`,
                                    height: '100%',
                                    backgroundColor: getPasswordStrengthColor(),
                                    transition: 'width 0.2s ease, background-color 0.2s ease',
                                }}
                            />
                        </Box>
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                        >
                            {getPasswordStrengthMessage(password)}
                        </Typography>
                    </Box>
                </Stack>
            </PassWordGuidelinesWrapper>
        </Stack>
    );
};
